import { ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, catchError, combineLatest, concatMap, filter, iif, map, merge, Observable, of, shareReplay, startWith, Subject, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs';
import {
	LikutDialogDataInput,
	LikutPaletteMoves,
	LikutPaletteMovesAllInventory,
	LikutPaletteMovesAllInventoryDisplay,
	LikutPalletValidation,
	LikutWarehouse,
	LikutWarehouseInventoryDisplay,
	LikutWarehouseInventoryDisplayWithScans,
	LikutWarehouseInventoryDisplayWithScansInstockString,
	LikutWarehouseInventoryPopupWithScanInfo,
	LikutWarehouseInventoryResponse,
	LikutWarehouseMoveResponse,
} from 'src/app/Interfaces/likut';
import { Users } from 'src/app/Interfaces/users';
import { ButtonTextPipe } from 'src/app/Pipes/button-text.pipe';
import { AlertService } from 'src/app/services/alert.service';
import { BarcodeService } from 'src/app/services/barcode.service';
import { BottomPopupService } from 'src/app/services/bottom-popup.service';
import { ButtonTextPossibleValues, LikutWarehouseService } from 'src/app/services/likut-warehouse.service';
import { LikutService } from 'src/app/services/likut.service';
import { LoadingService } from 'src/app/services/loading.service';
import { StoreService } from 'src/app/services/store.service';
import { UserService } from 'src/app/services/user.service';
import { ExcelObject, formatAsDateOptions, UtilService } from 'src/app/services/util.service';
import { MAX_INT32 } from 'src/app/Utility/constants';
import { utils, WorkBook, writeFile } from 'xlsx';
import { LikutWarehouseDialogComponent } from '../likut-warehouse/likut-warehouse-dialog.component';

type InventoryReportPopupHeader = {
	key: string;
	textID: string;
	isDate?: boolean;
	isButton?: boolean;
	shouldTranslateCount?: boolean;
};

type InventoryReportDisplayHeader = { key: string; headerClass: string; bodyClass: string; textID: ButtonTextPossibleValues; isDate?: boolean; displayValid?: boolean };

type RadioValues = 'basic' | 'detailed';

@Component({
	selector: 'app-likut-warehouse-reports',
	templateUrl: './likut-warehouse-reports.component.html',
	styleUrls: ['./likut-warehouse-reports.component.scss'],
	// changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LikutWarehouseReportsComponent implements OnInit, OnDestroy {
	_likutWarehouse = inject(LikutWarehouseService);
	_util = inject(UtilService);
	private _likut = inject(LikutService);
	private router = inject(Router);
	private fb = inject(FormBuilder);
	private _loading = inject(LoadingService);
	private _barcode = inject(BarcodeService);
	private _bottomPopup = inject(BottomPopupService);
	private _alert = inject(AlertService);
	private route = inject(ActivatedRoute);
	private dialog = inject(MatDialog);
	private _ss = inject(StoreService);
	private _user = inject(UserService);

	constructor() {}

	form = this.fb.nonNullable.group({
		search: [''],
		warehouses: [<string[]>[]],
	});

	excelRadioControl = new FormControl<RadioValues>('basic');
	radioButtons = [
		{ value: 'basic', text: 'מסוכם' },
		{ value: 'detailed', text: 'מפורט' },
	];

	pageMode$ = new BehaviorSubject<'inventory-report' | 'inventory-scan' | 'all-inventory-report' | 'all-inventory-report-specific-movement' | 'pallet-movements-report'>('inventory-report');

	title$ = combineLatest([this.pageMode$, this._likutWarehouse.buttonsAndText$]).pipe(
		map(([mode, buttonsAndText]) => {
			switch (mode) {
				case 'inventory-report':
					return buttonsAndText['main_inventory_report'];
				case 'inventory-scan':
					return buttonsAndText['inventory-scan-title'];
				case 'all-inventory-report':
				case 'all-inventory-report-specific-movement':
					return buttonsAndText['all-inventory-report-title'];
				case 'pallet-movements-report':
					return buttonsAndText['pallet-movements-report-title'];

				default:
					return buttonsAndText['main_inventory_report'];
			}
		}),
	);
	showFilterPopup = false;

	shouldDisplaySearchAndFilterAndReset$ = this.pageMode$.pipe(map(mode => mode !== 'inventory-scan'));
	specificMovementInfo$ = new BehaviorSubject<LikutWarehouseMoveResponse | null>(null);
	specificMovementID$ = new BehaviorSubject<string | null>(null);

	readonly defaultBodyClass = 'report-table__table-body-quantity';
	readonly defaultHeaderClass = 'report-table__table-head-quantity';
	readonly defaultClasses = {
		headerClass: this.defaultHeaderClass,
		bodyClass: this.defaultBodyClass,
	};
	readonly defaultClassesWidth60 = {
		headerClass: this.defaultHeaderClass + ' width-60',
		bodyClass: this.defaultBodyClass + ' width-60',
	};
	readonly defaultClassesWidth120 = {
		headerClass: this.defaultHeaderClass + ' width-120',
		bodyClass: this.defaultBodyClass + ' width-120',
	};

	buttonsAndText = ButtonTextPipe.prototype.transform;

	headers$: Observable<Array<InventoryReportDisplayHeader>> = this.pageMode$.pipe(
		map(mode => {
			switch (mode) {
				case 'inventory-report':
					return [
						{
							key: 'ProductId',
							headerClass: 'report-table__table-head-name width-60',
							bodyClass: 'report-table__table-body-name width-60',
							textID: 'inventory-report-productID-header',
						},
						{
							key: 'ProductName',
							headerClass: 'report-table__table-head-code width-120',
							bodyClass: 'report-table__table-body-quantity width-120',
							textID: 'inventory-report-product-name-header',
						},
						{
							...this.defaultClassesWidth60,
							key: 'WarehouseID',
							textID: 'inventory-report-warehouse-header',
						},
						{
							...this.defaultClassesWidth60,
							key: 'instock',
							textID: 'inventory-report-instock-header',
						},
						{
							...this.defaultClassesWidth60,
							key: 'PackQTY',
							textID: 'inventory-report-quantity-header',
						},
						{
							...this.defaultClassesWidth120,
							key: 'TokefDate',
							textID: 'inventory-report-tokef-header',
							isDate: true,
						},
					];
				case 'inventory-scan':
					return [
						{
							key: 'ProductId',
							headerClass: 'report-table__table-head-name width-60',
							bodyClass: 'report-table__table-body-name width-60',
							textID: 'inventory-scan-productID-header',
						},
						{
							key: 'ProductName',
							headerClass: 'report-table__table-head-code  width-120',
							bodyClass: 'report-table__table-body-quantity width-120',
							textID: 'inventory-scan-product-name-header',
						},
						{
							...this.defaultClassesWidth60,
							key: 'instock',
							textID: 'inventory-scan-quantity-header',
						},
						{
							...this.defaultClassesWidth60,
							key: 'totalScanned',
							textID: 'inventory-scan-totalScan-header',
							displayValid: true,
						},
					];

				case 'all-inventory-report':
					return [
						{
							key: 'ID',
							headerClass: 'report-table__table-head-name width-60',
							bodyClass: 'report-table__table-body-name width-60',
							textID: 'all-inventory-report-ID-header',
						},
						{
							...this.defaultClassesWidth120,
							key: 'DocCreateDate',
							textID: 'all-inventory-report-create-date-header',
							isDate: true,
						},
						{ key: 'UserID', headerClass: 'report-table__table-head-code width-120', bodyClass: 'report-table__table-body-quantity width-120', textID: 'all-inventory-report-userID-header' },
						{
							...this.defaultClassesWidth60,
							key: 'WarehouseID',
							textID: 'all-inventory-report-warehouse-header',
						},
					];

				case 'all-inventory-report-specific-movement':
					return [
						{
							key: 'PaletteID',
							headerClass: 'report-table__table-head-name width-60',
							bodyClass: 'report-table__table-body-name width-60',
							textID: 'all-inventory-report-palletID-header',
						},
						{
							...this.defaultClassesWidth60,
							key: 'ProductID',
							textID: 'all-inventory-report-productID-header',
						},
						{
							...this.defaultClassesWidth60,
							key: 'ProductName',
							textID: 'all-inventory-report-product-name-header',
						},
						{
							...this.defaultClassesWidth60,
							key: 'OrgWarehouseID',
							textID: 'all-inventory-report-org-warehouseID-header',
						},
					];

				case 'pallet-movements-report':
					return [
						{ key: 'PaletteID', headerClass: 'report-table__table-head-name width-60', bodyClass: 'report-table__table-body-name width-60', textID: 'pallet-movements-report-palletID-header' },
						{
							key: 'ProductId',
							headerClass: 'report-table__table-head-code width-120',
							bodyClass: 'report-table__table-body-quantity width-120',
							textID: 'pallet-movements-report-productID-header',
						},
						{
							key: 'ProductName',
							headerClass: 'report-table__table-head-code width-120',
							bodyClass: 'report-table__table-body-quantity width-120',
							textID: 'pallet-movements-report-product-name-header',
						},
						{
							key: 'WarehouseID',
							headerClass: 'report-table__table-head-code width-120',
							bodyClass: 'report-table__table-body-quantity width-120',
							textID: 'pallet-movements-report-warehouseID-header',
						},
						{
							key: 'Remark',
							headerClass: 'report-table__table-head-code width-120',
							bodyClass: 'report-table__table-body-quantity width-120',
							textID: 'pallet-movements-report-remark-header',
						},
					];
				default:
					return [];
			}
		}),
	);

	tableHeight: string | number = '';
	windowWidth = window.innerWidth;

	users$ = new BehaviorSubject<Users[]>([]);

	inventory$ = new BehaviorSubject<LikutWarehouseInventoryPopupWithScanInfo[]>([]);
	inventoryMovements$ = new BehaviorSubject<LikutPaletteMovesAllInventory[]>([]);
	inventoryPalletMovements$ = new BehaviorSubject<LikutPalletValidation[]>([]);

	selectedLines$ = new BehaviorSubject<Array<LikutWarehouseInventoryPopupWithScanInfo>>([]);
	selectedLinesPalletMovements$ = new BehaviorSubject<Array<LikutPaletteMoves>>([]);
	selectedLinesPalletMovementsDisplay$ = combineLatest([this.selectedLinesPalletMovements$, this.users$]).pipe(
		map(([lines, users]) => lines.map(line => ({ ...line, UserID: line.UserID + ' - ' + users.find(u => u.AgentID === line.UserID)?.UserName || '' }))),
	);
	shouldDisplayRemarkColumn$ = this.selectedLines$.pipe(map(lines => lines.some(l => l.Remark)));

	popupHeaders$: Observable<Array<InventoryReportPopupHeader>> = combineLatest([this.pageMode$, this.shouldDisplayRemarkColumn$]).pipe(
		map(([mode, hasRemarks]) => {
			let textIDPrefix = 'inventory-report-';
			let headers: Array<InventoryReportPopupHeader> = [
				{
					key: 'PaletteID',
					textID: 'palletID-label',
				},
				{
					key: 'TokefDate',
					textID: 'tokef-label',
					isDate: true,
				},
				{
					key: 'PackQTY',
					textID: 'packQTY-label',
				},
			];

			if (mode === 'inventory-scan') {
				textIDPrefix = 'inventory-scan-';
				headers = [
					...headers,
					{
						key: 'WarehouseID',
						textID: 'warehouse-label',
					},
					{
						key: 'isScanned',
						textID: 'isScanned-label',
						isButton: true,
					},
				];
			}

			if (mode === 'pallet-movements-report') {
				textIDPrefix = 'pallet-movements-report-';
				headers = [
					{ key: 'MoveID', textID: 'moveID-label' },
					{ key: 'Count', textID: 'count-label', shouldTranslateCount: true },
					{ key: 'MoveDate', textID: 'move-date-label', isDate: true },
					{ key: 'WarehouseID', textID: 'warehouseID-header' },
					{ key: 'UserID', textID: 'userID-label' },
				];
			}

			if (mode === 'inventory-report' && hasRemarks) {
				headers = [
					...headers,
					{
						key: 'Remark',
						textID: 'remark-label',
					},
				];
			}

			return headers.map(header => ({ ...header, textID: textIDPrefix + header.textID }));
		}),
	);

	selectedLinesDisplay$ = this.pageMode$.pipe(
		switchMap(mode => {
			switch (mode) {
				case 'all-inventory-report-specific-movement':
					return this.selectedLinesPalletMovements$;
				default:
					return this.selectedLines$;
			}
		}),
	);

	isPopupOpen$ = this.selectedLines$.pipe(map(lines => lines.length > 0));
	isPalletMovementsPopupOpen$ = this.selectedLinesPalletMovements$.pipe(map(lines => lines.length > 0));

	inventoryFiltered$ = combineLatest([this.inventory$, this.form.controls.warehouses.valueChanges.pipe(startWith([])), this.form.controls.search.valueChanges.pipe(startWith(''))]).pipe(
		map(([inventory, warehouses, search]) => {
			if (warehouses.length) {
				inventory = inventory.filter(i => warehouses.includes(i.WarehouseID));
			}
			if (search) {
				const formatted = search.toLowerCase().trim();
				inventory = inventory.filter(i => i.ProductId?.toLowerCase().includes(formatted) || i.ProductName?.toLowerCase().includes(formatted));
			}
			return inventory;
		}),
		shareReplay(1),
	);

	displayValues$ = this.inventoryFiltered$.pipe(
		map(inventory => {
			let values = Object.values(
				inventory.reduce((acc, curr) => {
					const key = `${curr.ProductId}-${curr.WarehouseID}`;
					if (!acc[key]) {
						acc[key] = { ...this.createInventoryReportRecord(curr), TokefDate: new Date(curr.TokefDate) };
					}
					const record = acc[key];
					const tokef = new Date(curr.TokefDate);
					record.instock += curr.instock;
					record.PackQTY += curr.PackQTY;
					record.TokefDate = tokef.getTime() < record.TokefDate.getTime() ? tokef : record.TokefDate;
					return acc;
				}, {} as Record<string, LikutWarehouseInventoryDisplay>),
				// instead of breaking the key -> value pairs abstraction for the table, we just patch the string with the PackQTY
				// feel free to refactor this to a more readable solution
			);
			// .map(v => ({ ...v, instock: v.instock + ' (' + v.PackQTY + ')' }));
			return values;
		}),
	);
	displayValuesAllInventory$ = combineLatest([this.inventory$, this.form.controls.warehouses.valueChanges.pipe(startWith([])), this.form.controls.search.valueChanges.pipe(startWith(''))]).pipe(
		map(([inventory, warehouses, search]) => {
			let values = inventory as unknown as LikutWarehouseMoveResponse[];
			if (warehouses.length) {
				values = values.filter(v => warehouses.includes(v.WarehouseID));
			}
			if (search) {
				const formatted = search.toLowerCase().trim();
				values = values.filter(v => v.WarehouseID?.toLowerCase().includes(formatted) || v.UserID?.toLowerCase().includes(formatted));
			}
			return values;
		}),
	);
	displayValuesPalletMovements$ = combineLatest([
		this.inventoryPalletMovements$,
		this.form.controls.warehouses.valueChanges.pipe(startWith([])),
		this.form.controls.search.valueChanges.pipe(startWith('')),
	]).pipe(
		map(([inventory, warehouses, search]) => {
			let values = inventory;
			if (warehouses.length) {
				values = values.filter(v => warehouses.includes(v.WarehouseID));
			}
			if (search) {
				const formatted = search.toLowerCase().trim();
				values = values.filter(v => String(v.PaletteID)?.toLowerCase().includes(formatted) || v.ProductName?.toLowerCase().includes(formatted) || v.ProductId?.toLowerCase().includes(formatted));
			}
			return values;
		}),
	);
	displayValuesAllInventorySpecificMovement$ = combineLatest([
		this.inventoryMovements$,
		this.form.controls.warehouses.valueChanges.pipe(startWith([])),
		this.form.controls.search.valueChanges.pipe(startWith('')),
	]).pipe(
		map(([inventory, warehouses, search]) => {
			let values: LikutPaletteMovesAllInventoryDisplay[] = inventory.map(i => {
				const isOtherWarehouse = i.OrgWarehouseID !== i.WarehouseID;
				const isWarehouse99 = i.OrgWarehouseID === '99';
				const value = {
					...i,
					isOtherWarehouse,
					isWarehouse99,
					Color: i.Color || (isOtherWarehouse ? null : 'var(--valid-color)'),
					// TODO currently, implementation for this is not done, so we just set it to false. Eventually, this scenario should be handled.
					isOtherWarehouseAndNewItem: false,
				};
				return value;
			});

			if (warehouses.length) {
				values = values.filter(v => warehouses.includes(v.OrgWarehouseID));
			}
			if (search) {
				const formatted = search.toLowerCase().trim();
				values = values.filter(
					v =>
						v.WarehouseID?.toLowerCase().includes(formatted) ||
						v.OrgWarehouseID?.toLowerCase().includes(formatted) ||
						v.UserID?.toLowerCase().includes(formatted) ||
						String(v.PaletteID).toLowerCase().includes(formatted) ||
						v.ProductName?.toLowerCase().includes(formatted) ||
						v.ProductID?.toLowerCase().includes(formatted),
				);
			}
			return values;
		}),
	);

	scannedPallets$ = new BehaviorSubject<LikutWarehouseInventoryPopupWithScanInfo[]>([]);
	anyScanned$ = this.scannedPallets$.pipe(map(pallets => pallets.length > 0));
	totalValidScans$ = combineLatest([this.scannedPallets$, this._likutWarehouse.inventoryScanSelectedWarehouse$]).pipe(
		map(([pallets, selectedWarehouse]) => pallets.filter(p => p.WarehouseID === selectedWarehouse).length),
	);
	scannedPalletsDictionaryByProductID$ = this.scannedPallets$.pipe(
		map(pallets => {
			return pallets.reduce((acc, curr) => {
				const key = `${curr.ProductId}`;
				acc[key] ||= [];
				acc[key].push(curr);
				return acc;
			}, {} as Record<string, LikutWarehouseInventoryPopupWithScanInfo[]>);
		}),
	);

	barcodePopupForm = this.fb.nonNullable.group({
		barcode: [''],
	});
	palletIDPopupFormSubmit$ = new Subject<string>();
	isBarcodePopupOpen$ = new BehaviorSubject(false);
	@ViewChild('barcodeInput') barcodeInput: ElementRef<HTMLInputElement>;

	palletIDStream$ = merge(this.palletIDPopupFormSubmit$, this._barcode.createBarcodeStream());

	displayValuesScanInventory$ = combineLatest([this.inventory$, this.scannedPalletsDictionaryByProductID$, this._likutWarehouse.inventoryScanSelectedWarehouse$]).pipe(
		map(([inventory, scannedPallets, selectedWarehouse]) => {
			// add lines from scannedPallets to the inventory if they don't exist
			// this is done to add products scanned but not in the inventory
			const values = Object.values(
				inventory.reduce((acc, curr) => {
					const key = `${curr.ProductId}`;
					const record = acc[key] || this.createInventoryScanRecord(curr, { isOtherWarehouseAndNewItem: false, scannedPallets: scannedPallets[key], selectedWarehouse });
					acc[key] = record;
					if (curr.Color) acc[key].Color = curr.Color;
					record.totalScanned = scannedPallets[key]?.length || 0;
					const tokef = new Date(curr.TokefDate);
					record.instock += curr.instock;
					record.PackQTY += curr.PackQTY;
					record.TokefDate = tokef.getTime() < record.TokefDate.getTime() ? tokef : record.TokefDate;
					return acc;
				}, {} as Record<string, LikutWarehouseInventoryDisplayWithScans>),
			).map(v => ({ ...v, isScansValid: v.totalScanned === v.instock && !v.isOtherWarehouse, instock: v.instock + ' (' + v.PackQTY + ')' }));

			for (const [key, pallets] of Object.entries(scannedPallets)) {
				// get Color from the first pallet that has a truthy Color
				if (!values.some(v => v.ProductId === key)) {
					const record: LikutWarehouseInventoryDisplayWithScansInstockString = {
						...this.createInventoryScanRecord(pallets[0], { isOtherWarehouseAndNewItem: true, scannedPallets: pallets, selectedWarehouse }),
						instock: pallets.reduce((acc, curr) => acc + curr.instock, 0) + ' (' + pallets.reduce((acc, curr) => acc + curr.PackQTY, 0) + ')',
						totalScanned: pallets.length,
					};
					record.PackQTY = pallets.reduce((acc, curr) => acc + curr.PackQTY, 0);

					record.isOtherWarehouseAndNewItem = record.isOtherWarehouse && !values.some(v => v.ProductId === key);
					record.isScansValid = false;
					values.push(record);
				}

				values[values.findIndex(v => v.ProductId === key)].Color = pallets.find(p => p.Color)?.Color || '';
			}

			return values;
		}),
	);

	displayValuesPerPageMode$ = this.pageMode$.pipe(
		switchMap(mode => {
			switch (mode) {
				case 'inventory-report':
					return this.displayValues$;
				case 'inventory-scan':
					return this.displayValuesScanInventory$;
				case 'all-inventory-report':
					return this.displayValuesAllInventory$;
				case 'all-inventory-report-specific-movement':
					return this.displayValuesAllInventorySpecificMovement$;
				case 'pallet-movements-report':
					return this.displayValuesPalletMovements$;
				default:
					return of([]);
			}
		}),
	);

	isExcelButtonVisible$ = combineLatest([this.pageMode$, this._user.isAdmin$]).pipe(
		map(([mode, isAdmin]) => {
			switch (mode) {
				case 'inventory-report':
					return this._likut.isLikutManager || isAdmin;
				case 'pallet-movements-report':
				case 'all-inventory-report-specific-movement':
					return true;

				default:
					return false;
			}
		}),
	);
	shouldDisplayExcelRadioGroup$ = this.pageMode$.pipe(map(mode => mode !== 'pallet-movements-report'));

	shouldDisplayTotalPackAndInStock$ = this.pageMode$.pipe(map(mode => mode === 'inventory-report'));
	totalLines$ = this.displayValuesPerPageMode$.pipe(map(values => values.length));
	totalPackQTY$ = this.displayValues$.pipe(map(values => values.reduce((acc, curr) => acc + curr.PackQTY, 0)));
	totalInStock$ = this.displayValues$.pipe(map(values => values.reduce((acc, curr) => acc + curr.instock, 0)));

	warehouses$ = merge(this.inventory$, this.inventoryMovements$, this.inventoryPalletMovements$).pipe(
		filter(inventory => inventory.length > 0),
		map(inventory => (this.isRowPalletMovement(inventory[0]) ? Array.from(new Set(inventory.map(i => i.OrgWarehouseID))) : Array.from(new Set(inventory.map(i => i.WarehouseID))))),
	);

	destroy$ = new Subject<void>();

	errorMessage = '';

	buttonTextPipe = ButtonTextPipe.prototype.transform;

	openScanHistoryButtonTitle$ = this._likutWarehouse.buttonsAndText$.pipe(map(buttonsAndText => this.buttonTextPipe(buttonsAndText['inventory-scan-history-button-title'])));

	isHistoryPopupOpen$ = new BehaviorSubject(false);

	popupTitle$ = combineLatest([this.selectedLines$, this._likutWarehouse.buttonsAndText$, this.isHistoryPopupOpen$]).pipe(
		map(([selectedLines, buttonsAndText, isHistoryPopupOpen]) => {
			if (isHistoryPopupOpen) {
				return this.buttonTextPipe(buttonsAndText['inventory-scan-history-popup-title']);
			}

			if (selectedLines.length && selectedLines[0].ProductName && selectedLines[0].ProductId) {
				return selectedLines[0].ProductName + ' (' + selectedLines[0].ProductId + ')';
			}

			return this.buttonTextPipe(buttonsAndText['inventory-report-no-product-name']);
		}),
	);

	ngOnInit(): void {
		if (location.pathname.includes('inventory-scan')) {
			this.pageMode$.next('inventory-scan');
			this.setupBarcodeScanningLogic();
		}

		if (location.pathname.includes('all-inventory')) {
			this.pageMode$.next('all-inventory-report');
		}

		if (location.pathname.includes('all-inventory/')) {
			this.pageMode$.next('all-inventory-report-specific-movement');
			this.specificMovementID$.next(this.route.snapshot.paramMap.get('moveID'));
		}

		if (location.pathname.includes('pallet-movements')) {
			this.pageMode$.next('pallet-movements-report');
		}

		this.getInventoryReport();

		const textPipe = ButtonTextPipe.prototype.transform;

		this._likutWarehouse.buttonsAndText$.pipe(take(1)).subscribe(buttonsAndText => {
			this.errorMessage = textPipe(buttonsAndText['inventory-report-request-error']);
		});

		window.scrollTo(0, 0);

		setTimeout(() => {
			const [header, filterRow] = document.querySelectorAll('header, .filter-row');

			let height = header.getBoundingClientRect().height;

			if (filterRow) {
				height += filterRow.getBoundingClientRect().height;
			}

			// zoom: 1.2
			if (this.windowWidth > 767 && this.windowWidth < 1099) {
				height *= 2.2;
			}
			this.tableHeight = height + 'px';
		}, 0);
	}

	ngOnDestroy() {
		this._loading.changeLoadingStatuses({ isLoading: false });

		this.destroy$.next();
		this.destroy$.complete();
	}

	handleNavigation() {
		history.back();
	}

	Restart() {
		// location.reload();
		this.form.reset();
	}

	clearSearch() {
		this.form.reset();
	}

	resolveHandlePopupClick(line: LikutWarehouseInventoryDisplayWithScansInstockString | LikutPaletteMovesAllInventory | LikutPalletValidation | null) {
		if (!line) {
			this.selectedLines$.next([]);
			this.selectedLinesPalletMovements$.next([]);
			this.isHistoryPopupOpen$.next(false);
			return;
		}

		if (this.isLikutPalletValidation(line)) {
			this.handlePopupClickPalletMovementsReport(line.PaletteID);
			return;
		}

		if (this.isRowPalletMovement(line)) {
			if (this.pageMode$.value === 'all-inventory-report-specific-movement') {
				this.openMovementPopup({ pallet: line });
			}
			return;
		}

		if (this.pageMode$.value === 'all-inventory-report') {
			const row = line as unknown as LikutWarehouseMoveResponse;
			this._likutWarehouse.specificMovementInfoSelected$.next(row);
			this.router.navigate([row.ID], { relativeTo: this.route });
			return;
		}

		const { ProductId, WarehouseID } = line;

		if (this.pageMode$.value === 'inventory-report') {
			this.handlePopupClickInventoryReport({ ProductId, WarehouseID });
			return;
		}

		this.handlePopupClickInventoryScan(ProductId);
	}

	toggleAllScanHistoryPopup(mode: 'open' | 'close') {
		if (mode === 'close') {
			this.selectedLines$.next([]);
			return;
		}

		combineLatest([this.inventory$, this.scannedPalletsDictionaryByProductID$, this._likutWarehouse.inventoryScanSelectedWarehouse$])
			.pipe(take(1))
			.subscribe(([inventory, scannedPalletsDictionary, selectedWarehouse]) => {
				const linesToAdd: LikutWarehouseInventoryPopupWithScanInfo[] = [];
				const selectedLines: LikutWarehouseInventoryPopupWithScanInfo[] = inventory;
				const scanned = Object.values(scannedPalletsDictionary).flat();
				for (const selected of selectedLines) {
					const scannedPallet = scanned?.find(p => p.PaletteID === selected.PaletteID);
					if (scannedPallet) {
						selected.isScanned = true;
						selected.color = 'var(--valid-color)';

						if (scannedPallet.WarehouseID !== selectedWarehouse) {
							selected.color = selected.Color || 'yellow';
						}
					}
				}

				for (const pallet of scanned || []) {
					const notInCurrentWarehouse = pallet.WarehouseID !== selectedWarehouse;
					if (notInCurrentWarehouse) {
						pallet.color = pallet.Color || 'yellow';
						pallet.isScanned = true;
						linesToAdd.push(pallet);
					}
				}

				this.selectedLines$.next([...selectedLines.filter(l => l.isScanned), ...linesToAdd].map(l => ({ ...l })));
			});
	}

	prepareToSendData() {
		this._likutWarehouse.buttonsAndText$.pipe(take(1)).subscribe(async buttonsAndText => {
			const text = this.buttonTextPipe(buttonsAndText['inventory-scan-confirm-send']);
			const result = await this._alert.customConfirmAlert(text);
			if (!result) return;
			this._loading.changeLoadingStatuses({ isLoading: true });
			const body = this._likutWarehouse.prepareDataForSendInventoryScanReport(this.scannedPallets$.value);
			this._likutWarehouse.sendInventoryScanReport(body).subscribe({
				next: data => {
					this._loading.changeLoadingStatuses({ isLoading: false });
					if (typeof data === 'string') {
						this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-network-error']));
						return;
					}

					const id = data.lastID;
					this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-inventory-scan-sent']) + ' ' + id);
					history.back();
				},
				error: err => {
					this._loading.changeLoadingStatuses({ isLoading: false });
					this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-network-error']));
				},
			});
		});
	}

	handlePopupClickInventoryReport(line: { ProductId; WarehouseID }) {
		const { ProductId, WarehouseID } = line;
		this.selectedLines$.next(this.inventory$.value.filter(line => line.WarehouseID === WarehouseID && line.ProductId === ProductId));
	}

	handlePopupClickInventoryScan(ProductId) {
		// const { ProductId } = line;
		combineLatest([this.inventory$, this.scannedPalletsDictionaryByProductID$, this._likutWarehouse.inventoryScanSelectedWarehouse$])
			.pipe(take(1))
			.subscribe(([inventory, scannedPalletsDictionary, selectedWarehouse]) => {
				const linesToAdd: LikutWarehouseInventoryPopupWithScanInfo[] = [];
				const selectedLines: LikutWarehouseInventoryPopupWithScanInfo[] = inventory.filter(i => i.ProductId === ProductId);
				const scanned = scannedPalletsDictionary[ProductId];
				for (const selected of selectedLines) {
					const scannedPallet = scanned?.find(p => p.PaletteID === selected.PaletteID);
					if (scannedPallet) {
						selected.isScanned = true;
						selected.color = 'var(--valid-color)';

						if (scannedPallet.WarehouseID !== selectedWarehouse) {
							selected.color = scannedPallet.WarehouseID === '99' ? 'var(--invalid-color)' : 'yellow';
						}
					}
				}

				for (const pallet of scanned || []) {
					const notInCurrentWarehouse = pallet.WarehouseID !== selectedWarehouse;
					if (notInCurrentWarehouse) {
						pallet.color = pallet.WarehouseID === '99' ? 'var(--invalid-color)' : 'yellow';
						pallet.isScanned = true;
						linesToAdd.push(pallet);
					}
				}

				this.selectedLines$.next([...selectedLines, ...linesToAdd].map(l => ({ ...l })));
			});
	}

	handlePopupClickPalletMovementsReport(palletID: string | number) {
		this._likutWarehouse.getPalletMovementsReport(palletID).subscribe({
			next: data => {
				if (typeof data === 'string') {
					alert(this.errorMessage);
					return;
				}

				this.selectedLinesPalletMovements$.next(data.recordset);

				if (!this.users$.value?.length) {
					this._ss.GetAllAgents().subscribe({
						next: data => {
							if (typeof data === 'string') {
								alert(this.errorMessage);
								return;
							}
							this.users$.next(data.recordset);
						},
						error: err => {},
					});
				}
			},
			error: err => {
				alert(this.errorMessage);
			},
		});
	}

	removeScannedPallet(palletID: number) {
		combineLatest([this._likutWarehouse.buttonsAndText$, this.inventory$, this.scannedPallets$])
			.pipe(take(1))
			.subscribe(([buttonsAndText, inventory, scanned]) => {
				const pallet = scanned.find(p => p.PaletteID === palletID);
				if (pallet) {
					pallet.isScanned = false;
					pallet.color = null;
					const palletInInventory = inventory.find(i => i.PaletteID === palletID);
					if (palletInInventory) {
						palletInInventory.isScanned = false;
						palletInInventory.color = null;
					}
				}
				this.scannedPallets$.next([...scanned.filter(p => p.PaletteID !== palletID)]);
				const selectedLines$ = this.selectedLines$.value.filter(line => line.PaletteID !== palletID || line.isInInventory);
				// this.selectedLines$.next([]);
				this.inventory$.next(inventory.map(i => ({ ...i })));
				this.selectedLines$.next(selectedLines$.map(line => (line.PaletteID === palletID ? { ...line, isScanned: false, color: null } : { ...line })));
				this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-pallet-scan-removed']));
			});
	}

	setupBarcodeScanningLogic() {
		this.isBarcodePopupOpen$.pipe(takeUntil(this.destroy$)).subscribe(isOpen => {
			if (isOpen) {
				setTimeout(() => {
					this.barcodeInput.nativeElement.focus();
				}, 0);
			}
		});

		this.isHistoryPopupOpen$.pipe(takeUntil(this.destroy$)).subscribe(isOpen => {
			if (isOpen) {
				this.toggleAllScanHistoryPopup('open');
			} else {
				this.toggleAllScanHistoryPopup('close');
			}
		});

		this.palletIDStream$
			.pipe(
				takeUntil(this.destroy$),
				withLatestFrom(this.scannedPallets$, this._likutWarehouse.buttonsAndText$),
				concatMap(([PaletteID, scannedPallets, buttonsAndText]) =>
					iif(
						// sql server column type is int, so we need to check if the value is within the range of int32
						() => +PaletteID <= MAX_INT32 && !scannedPallets.some(p => p.PaletteID === +PaletteID),
						this._likutWarehouse.checkIfInventoryScanPalletIDValid(PaletteID).pipe(
							map(pallet => ({ PaletteID, pallet, networkError: false, buttonsAndText })),
							catchError(error => {
								this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-network-error']));
								return of({ PaletteID, pallet: null, networkError: true, buttonsAndText });
							}),
						),
						of({ PaletteID, pallet: null, networkError: false, buttonsAndText }),
					),
				),
			)
			.subscribe({
				next: ({ PaletteID, pallet, networkError, buttonsAndText }) => {
					if (!PaletteID || networkError) return;
					if (this.scannedPallets$.value.some(p => p.PaletteID === +PaletteID)) {
						this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-pallet-already-in-list']));
						return;
					}
					if (pallet && !this.scannedPallets$.value.some(p => p.PaletteID === +PaletteID)) {
						this.scannedPallets$.next([...this.scannedPallets$.value, pallet]);
						const productID = pallet.ProductId;
						if (this.pageMode$.value === 'inventory-scan' && this.selectedLines$.value?.[0]?.ProductId === productID) {
							this.handlePopupClickInventoryScan(productID);
						}
						this._bottomPopup.show(this.buttonTextPipe(buttonsAndText['snackbar-pallet-scanned']));
						return;
					}
					if (!pallet) {
						alert(this.buttonTextPipe(buttonsAndText['inventory-scan-no-pallet-found']));
					}
				},
			});
	}

	getInventoryReport() {
		switch (this.pageMode$.value) {
			case 'inventory-scan':
				this._likutWarehouse
					.getInventoryScanReport()
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: data => {
							if (typeof data === 'string' || !data) {
								alert(this.errorMessage);
								history.back();
								return;
							}

							this.inventory$.next(data.recordset.map(i => ({ ...i, isInInventory: true })));
						},
						error: err => {
							alert(this.errorMessage);
							history.back();
						},
					});
				break;

			case 'all-inventory-report':
				this._likutWarehouse
					.getAllInventoryReport()
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: data => {
							if (typeof data === 'string') {
								alert(this.errorMessage);
								history.back();
								return;
							}

							this.inventory$.next(data.recordset.map(i => ({ ...i, isInInventory: true })));
						},
						error: err => {
							alert(this.errorMessage);
							history.back();
						},
					});

				break;
			case 'all-inventory-report-specific-movement':
				this._likutWarehouse
					.getAllInventoryReportMovements(this.route.snapshot.params['moveID'])
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: data => {
							if (typeof data === 'string') {
								alert(this.errorMessage);
								history.back();
								return;
							}

							this.inventoryMovements$.next(data.recordset);
						},
						error: err => {
							alert(this.errorMessage);
							history.back();
						},
					});

				break;

			case 'inventory-report':
				this._likutWarehouse
					.getPalletInventoryReport()
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: data => {
							if (typeof data === 'string') {
								alert(this.errorMessage);
								history.back();
								return;
							}

							this.inventory$.next(data.recordset.map(i => ({ ...i, isInInventory: true })));
						},
						error: err => {
							alert(this.errorMessage);
							history.back();
						},
					});
				break;

			case 'pallet-movements-report':
				this._likutWarehouse
					.getAllPalletMovementsReport()
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: data => {
							if (typeof data === 'string') {
								alert(this.errorMessage);
								history.back();
								return;
							}

							this.inventoryPalletMovements$.next(data.recordset);
						},
						error: err => {
							alert(this.errorMessage);
							history.back();
						},
					});
				break;

			default:
				console.log('i shouldnt appear');
		}
	}

	onBarcodeFormSubmit() {
		const { barcode } = this.barcodePopupForm.controls;
		if (!barcode.value) return;
		this.palletIDPopupFormSubmit$.next(barcode.value);
		this.barcodePopupForm.reset();
	}

	createInventoryReportRecord(pallet: LikutWarehouseInventoryPopupWithScanInfo) {
		return {
			ProductId: pallet.ProductId,
			ProductName: pallet.ProductName,
			WarehouseID: pallet.WarehouseID,
			instock: 0,
			PackQTY: 0,
			Color: pallet.Color,
		};
	}

	createInventoryScanRecord(
		pallet: LikutWarehouseInventoryPopupWithScanInfo,
		options: {
			selectedWarehouse: string;
			isOtherWarehouseAndNewItem: boolean;
			scannedPallets?: LikutWarehouseInventoryPopupWithScanInfo[];
		},
	): LikutWarehouseInventoryDisplayWithScans {
		const { selectedWarehouse, scannedPallets, isOtherWarehouseAndNewItem } = options;
		const isWarehouse99 = scannedPallets?.find(p => p.WarehouseID === '99') !== undefined;
		const isOtherWarehouse = scannedPallets?.find(p => p.WarehouseID !== selectedWarehouse) !== undefined;
		const isScansValid = scannedPallets?.length === pallet.instock;
		return {
			ProductId: pallet.ProductId,
			ProductName: pallet.ProductName,
			WarehouseID: pallet.WarehouseID,
			instock: 0,
			TokefDate: new Date(),
			PackQTY: 0,
			totalScanned: 0,
			isWarehouse99,
			isOtherWarehouse,
			isOtherWarehouseAndNewItem,
			isScansValid,
			Color: pallet.Color,
		};
	}

	openMovementPopup(body: { pallet: LikutPaletteMovesAllInventory }) {
		const { pallet } = body;
		let data: LikutDialogDataInput = {
			buttonText: '',
			titleToDisplay: '',
			mode: 'transferFromAllInventoryReport',
			startWithPallet: this.createLikutValidationFromLikutMovesAllInventory(pallet),
		};

		this._likutWarehouse.updateWarehousesAll();

		// get title from observable, but before the switch statement
		this._likutWarehouse.buttonsAndText$.pipe(take(1)).subscribe(val => {
			const titleToDisplay = this.buttonsAndText(val['pallet-transfer-title-next-stage']);
			const buttonText = this.buttonsAndText(val['pallet-transfer-title-next-stage-button']);
			data = { ...data, titleToDisplay, buttonText };
		});

		this.dialog.open(LikutWarehouseDialogComponent, {
			direction: 'rtl',
			width: '95vw',
			maxWidth: '95vw',
			data,
		});
	}

	downloadExcel() {
		const dataObject: ExcelObject = { data: [], filename: '', extension: 'xlsx' };
		if (this.pageMode$.value === 'all-inventory-report-specific-movement') {
			this.generateSpecificMovementExcel(dataObject);
			return;
		}

		if (this.excelRadioControl.value === 'basic') {
			this.generateRegularExcel(dataObject);
			return;
		}

		const getFormatOptions = (str: 'date' | 'hour'): formatAsDateOptions => ({ isTrueISO: true, onlyTime: str === 'hour' });

		const longestStringInColumn: any = {};

		this.inventoryFiltered$.pipe(take(1)).subscribe(values => {
			dataObject.data = values.map(b => {
				const obj: { [key: string]: string | number } = {
					'מק"ט': b.ProductId,
					משטח: b.PaletteID,
					פריט: b.ProductName,
					מחסן: b.WarehouseID,
					תוקף: this._util.formatAsDate(b.TokefDate, getFormatOptions('date')),
					כמות: b.PackQTY,
					הערה: b.Remark,
				};

				for (const entry of Object.entries(obj)) {
					longestStringInColumn[entry[0]] ??= 0;
					longestStringInColumn[entry[0]] = Math.max(String(entry[1]).length, longestStringInColumn[entry[0]], 3);
				}
				return obj;
			});

			this.title$.pipe(take(1)).subscribe(title => {
				const buttonLevelText = this.radioButtons.find(r => r.value === this.excelRadioControl.value)?.text ?? '';
				dataObject.filename = `${this._util.removeProblematicCharacters(this.buttonTextPipe(title))} - ${buttonLevelText}`;
			});

			// dataObject.filename = document.title;

			const ws = utils.json_to_sheet(dataObject.data);

			ws['!cols'] = Object.values(longestStringInColumn).map((columnWidth: number) => ({ wch: columnWidth }));

			const wb = utils.book_new();
			if (!wb.Workbook) wb.Workbook = {};
			if (!wb.Workbook.Names) wb.Workbook.Views = [{}];
			wb.Workbook.Views[0].RTL = true;
			utils.book_append_sheet(wb, ws, dataObject.filename);
			writeFile(wb, `${dataObject.filename}.${dataObject.extension}`, { bookType: dataObject.extension, sheet: dataObject.filename, compression: true, cellStyles: true });
		});
	}

	// getExcelDataAndTitle(): ExcelObject {
	// 	const dataObject: ExcelObject = { data: [], filename: '', extension: 'xlsx' };

	// 	const getFormatOptions = (str: 'date' | 'hour'): formatAsDateOptions => ({ isTrueISO: true, onlyTime: str === 'hour' });

	// 	return dataObject;
	// }

	generateRegularExcel(dataObject: ExcelObject): void {
		const getFormatOptions = (str: 'date' | 'hour'): formatAsDateOptions => ({ isTrueISO: true, onlyTime: str === 'hour' });

		const longestStringInColumn: any = {};

		this.displayValues$.pipe(take(1)).subscribe(values => {
			dataObject.data = values.map(b => {
				const obj: { [key: string]: string | number } = {
					'מק"ט': b.ProductId,
					פריט: b.ProductName,
					מחסן: b.WarehouseID,
					משטחים: b.instock,
					מלאי: b.PackQTY,
					תוקף: this._util.formatAsDate(b.TokefDate, getFormatOptions('date')),
				};

				for (const entry of Object.entries(obj)) {
					longestStringInColumn[entry[0]] ??= 0;
					longestStringInColumn[entry[0]] = Math.max(String(entry[1]).length, longestStringInColumn[entry[0]], 3);
				}
				return obj;
			});

			this.title$.pipe(take(1)).subscribe(title => {
				const buttonLevelText = this.radioButtons.find(r => r.value === this.excelRadioControl.value)?.text ?? '';
				dataObject.filename = `${this._util.removeProblematicCharacters(this.buttonTextPipe(title))} - ${buttonLevelText}`;
			});

			// dataObject.filename = document.title;

			const ws = utils.json_to_sheet(dataObject.data);

			ws['!cols'] = Object.values(longestStringInColumn).map((columnWidth: number) => ({ wch: columnWidth }));

			const wb = utils.book_new();
			if (!wb.Workbook) wb.Workbook = {};
			if (!wb.Workbook.Names) wb.Workbook.Views = [{}];
			wb.Workbook.Views[0].RTL = true;
			utils.book_append_sheet(wb, ws, dataObject.filename);
			writeFile(wb, `${dataObject.filename}.${dataObject.extension}`, { bookType: dataObject.extension, sheet: dataObject.filename, compression: true, cellStyles: true });
		});
	}

	generateSpecificMovementExcel(dataObject: ExcelObject): void {
		const longestStringInColumn: any = {};

		if (!this._likutWarehouse.specificMovementInfoSelected$.value) {
			history.back();
			return;
		}

		this.displayValuesAllInventorySpecificMovement$.pipe(take(1)).subscribe(values => {
			dataObject.data = values.map(b => {
				const obj: { [key: string]: string | number } = {
					משטח: b.PaletteID,
					'מק"ט': b.ProductID,
					פריט: b.ProductName,
					מחסן: b.OrgWarehouseID,
				};

				for (const entry of Object.entries(obj)) {
					longestStringInColumn[entry[0]] ??= 0;
					longestStringInColumn[entry[0]] = Math.max(String(entry[1]).length, longestStringInColumn[entry[0]], 3);
				}
				return obj;
			});

			this.title$.pipe(take(1)).subscribe(title => {
				const infoRow = this._likutWarehouse.specificMovementInfoSelected$.value;
				dataObject.filename = this._util.removeProblematicCharacters(`${this.buttonTextPipe(title)}_תנועה-${this.specificMovementID$.value}_סוכן-${infoRow.UserID}_מחסן-${infoRow.WarehouseID}`);
			});

			// dataObject.filename = document.title;

			const ws = utils.json_to_sheet(dataObject.data);

			ws['!cols'] = Object.values(longestStringInColumn).map((columnWidth: number) => ({ wch: columnWidth }));

			const wb = utils.book_new();
			if (!wb.Workbook) wb.Workbook = {};
			if (!wb.Workbook.Names) wb.Workbook.Views = [{}];
			wb.Workbook.Views[0].RTL = true;
			utils.book_append_sheet(wb, ws, 'תנועה');
			writeFile(wb, `${dataObject.filename}.${dataObject.extension}`, { bookType: dataObject.extension, compression: true, cellStyles: true });
		});
	}

	getBackgroundColor(row: LikutWarehouseInventoryDisplayWithScans): string {
		if (row.Color) return row.Color;

		// if (row.isWarehouse99) {
		// 	//red
		// 	return 'rgb(255, 0, 0)';
		// }
		if (row.isOtherWarehouseAndNewItem) {
			// orange
			return 'rgb(255, 165, 0)';
		}
		if (row.isOtherWarehouse) {
			// yellow
			return 'rgb(255, 255, 0)';
		}
		return '';
	}

	createLikutValidationFromLikutMovesAllInventory(row: LikutPaletteMovesAllInventory): LikutPalletValidation {
		return {
			ProductId: row.ProductID,
			ProductName: row.ProductName,
			WarehouseID: row.OrgWarehouseID,
			TokefDate: new Date(row.TokefDate),
			CreateDate: new Date(row.CreateDate),
			instock: row.instock,
			PaletteID: row.PaletteID,
			PackQTY: row.PackQTY,
			color: '',
		};
	}

	isRowPalletMovement(
		row:
			| LikutWarehouseInventoryDisplayWithScansInstockString
			| LikutWarehouseInventoryPopupWithScanInfo
			| LikutWarehouseInventoryDisplay
			| LikutPaletteMovesAllInventory
			| LikutPaletteMoves
			| LikutPalletValidation,
	): row is LikutPaletteMovesAllInventory {
		return 'OrgWarehouseID' in row;
	}

	isLikutPaletteMoves(
		row: LikutPaletteMoves | LikutWarehouseInventoryPopupWithScanInfo | LikutWarehouseInventoryDisplayWithScansInstockString | LikutPaletteMovesAllInventory,
	): row is LikutPaletteMoves {
		return row && 'Count' in row && 'Extra1' in row;
	}

	isLikutPalletValidation(row: LikutWarehouseInventoryDisplayWithScansInstockString | LikutPaletteMovesAllInventory | LikutPalletValidation): row is LikutPalletValidation {
		return row && !('MoveID' in row) && 'PaletteID' in row && typeof row.instock === 'number';
	}
}
